import React from 'react';
import SourceChip, { type SourceChipVariant } from './SourceChip';

export type SourceChipsProps = {
  sources: readonly string[];
  /** Per-source enabled flag; omitted sources default to enabled. */
  enabledBySource?: Readonly<Record<string, boolean>>;
  variant?: SourceChipVariant;
  mode?: 'static' | 'toggle';
  selected?: Set<string> | readonly string[];
  onToggle?: (source: string) => void;
  disabled?: boolean;
  emptyLabel?: string;
  className?: string;
  'aria-label'?: string;
};

function toSelectedSet(selected: Set<string> | readonly string[] | undefined): Set<string> {
  if (!selected) return new Set();
  return selected instanceof Set ? selected : new Set(selected);
}

/** Inline list of source chips with consistent spacing. */
export default function SourceChips({
  sources,
  enabledBySource,
  variant = 'compact',
  mode = 'static',
  selected,
  onToggle,
  disabled = false,
  emptyLabel = '—',
  className = '',
  'aria-label': ariaLabel = 'Sources',
}: SourceChipsProps) {
  if (!sources.length) {
    return <span className={className || 'muted'}>{emptyLabel}</span>;
  }

  const selectedSet = toSelectedSet(selected);
  const isToggle = mode === 'toggle' && onToggle;

  return (
    <div
      className={['sources-chips sources-chips--inline', className].filter(Boolean).join(' ')}
      role="list"
      aria-label={ariaLabel}
    >
      {sources.map((name) => {
        const enabled = enabledBySource?.[name] ?? true;
        if (isToggle) {
          return (
            <SourceChip
              key={name}
              source={name}
              enabled={enabled}
              variant={variant}
              mode="toggle"
              selected={selectedSet.has(name)}
              disabled={disabled}
              onClick={() => onToggle(name)}
            />
          );
        }
        return (
          <SourceChip
            key={name}
            source={name}
            enabled={enabled}
            variant={variant}
            mode="static"
          />
        );
      })}
    </div>
  );
}
